import React from "react";
import ReactDOM from "react-dom/client";

import * as Sentry from "@sentry/react";

import { createRouter, RouterProvider } from "@tanstack/react-router";

// both imported for their side effects, and both must be evaluated before anything they
// affect: the dayjs plugins before any module that formats a date, the reload listener
// before the first lazy chunk import can fail
import "./lib/dayjsPlugins.ts";
import "./lib/staleDeployReload.ts";
import { routeTree } from "./routeTree.gen.ts";

Sentry.init({
  dsn: "https://9f7b04b6ae6b273865724e0b2636885d@o4508989841408001.ingest.us.sentry.io/4508989843832833",
  environment: import.meta.env.MODE || 'development',
  enabled: import.meta.env.MODE !== 'development',
});

const router = createRouter({
  routeTree,
  // without this the router still sets history.scrollRestoration = "manual",
  // so the browser's own restoration is off and nothing replaces it
  scrollRestoration: true,
  // key by path rather than by history entry: the tabs are a bottom tab bar, so
  // returning to one should land where you left it whether you got there with
  // Back or by tapping the tab again. it also means opening a detail (`?v=`)
  // shares the list's key instead of starting a fresh one
  getScrollRestorationKey: (location) => location.pathname,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// download the route chunks in parallel with the Convex auth handshake,
// since RouterProvider doesn't mount until <Authenticated> resolves
router.preloadRoute({ to: "/" }).catch(() => {});

import { ConvexAuthProvider } from "@convex-dev/auth/react";
import { ConvexQueryCacheProvider } from "convex-helpers/react/cache";
import {
  Authenticated,
  AuthLoading,
  ConvexReactClient,
  Unauthenticated,
} from "convex/react";

import { MantineProvider } from "@mantine/core";

import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import '@mantine/tiptap/styles.css';
import "yet-another-react-lightbox/styles.css";

import { DatesProvider } from "@mantine/dates";

import { FullPageLoader } from "./components/FullPageLoader";
import Landing from "./components/Landing.tsx";

import { theme } from "./theme.ts";

import "./index.css";

const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL as string);
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      {/* Convex Auth strips `?code=` after OAuth, and its default replaceURL is
          `history.replaceState({}, "", url)` — an empty state object, which
          discards TanStack's __TSR_index/__TSR_key. The router's history exists
          from module scope (createRouter, above), so this lands on a live
          history even though RouterProvider mounts later, and TSR never
          re-seeds: it only assigns a key at creation. Routing the rewrite
          through the router's own replace keeps the index intact, which the
          detail stack depends on to close by going back. */}
      <ConvexAuthProvider
        client={convex}
        replaceURL={(url) => { router.history.replace(url); }}
      >
        <ConvexQueryCacheProvider>
          <MantineProvider theme={theme}>
            <DatesProvider settings={{}}>
              <Unauthenticated>
                <Landing />
              </Unauthenticated>

              <AuthLoading>
                <FullPageLoader />
              </AuthLoading>

              <Authenticated>
                <RouterProvider router={router} />
              </Authenticated>
            </DatesProvider>
          </MantineProvider>
        </ConvexQueryCacheProvider>
      </ConvexAuthProvider>
    </React.StrictMode>
  );
}
